import {FilterProps, sortingDirectionOptions} from "../tableTypes";
import React, {ReactNode, useEffect, useState} from "react";

interface TableContextProps {
    filtersBase: FilterProps[];
    setFiltersBase: any;
    filters: FilterProps[];
    setFilters: any;
    sortingBy: string;
    setSortingBy: any;
    sortingDirection: sortingDirectionOptions;
    setSortingDirection: any;
    data: any[];
    setData: any;
    locallyFilteredData: any[];
    setLocallyFilteredData: any;
    isLoading: boolean;
    setIsLoading: (newIsLoading: boolean) => void;
    total: number;
    setTotal: (newNumber: number) => void;
    locallyFilteredTotal: number;
    setLocallyFilteredTotal: (newNumber: number) => void;
    isFiltersPanelOpen: boolean;
    setIsFiltersPanelOpen: (newState: boolean) => void;
    isColumnsPanelOpen: boolean;
    setIsColumnsPanelOpen: (newState: boolean) => void;
    page: number;
    setPage: (newPage: number) => void;
    pageSize?: number;
}

interface TableContextProviderProps {
    children: ReactNode | ReactNode[];
    initialFilters?: FilterProps[];
    pageSize?: number;
}

export const TableContext = React.createContext<TableContextProps>({
    filtersBase: [],
    setFiltersBase: () => null,
    filters: [],
    setFilters: () => null,
    sortingBy: '',
    setSortingBy: () => null,
    sortingDirection: sortingDirectionOptions.ASC,
    setSortingDirection: () => null,
    data: [],
    setData: () => null,
    locallyFilteredData: [],
    setLocallyFilteredData: () => null,
    isLoading: false,
    setIsLoading: (newIsLoading: boolean) => null,
    total: 0,
    setTotal: (newTotal: number) => null,
    locallyFilteredTotal: 0,
    setLocallyFilteredTotal: (newTotal: number) => null,
    isFiltersPanelOpen: false,
    setIsFiltersPanelOpen: (newState: boolean) => null,
    isColumnsPanelOpen: false,
    setIsColumnsPanelOpen: (newState: boolean) => null,
    page: 0,
    setPage: (newPage: number) => null,
    pageSize: 50,
});

export const TableContextProvider = ({ children, initialFilters, pageSize = 50 }: TableContextProviderProps) => {
    const [filtersBase, setFiltersBase] = useState(initialFilters || []);
    const [filters, setFilters] = useState<FilterProps[]>(initialFilters || []);
    const [sortingBy, setSortingBy] = useState('');
    const [sortingDirection, setSortingDirection] = useState(sortingDirectionOptions.DESC);
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [isFiltersPanelOpen, _setIsFiltersPanelOpen] = useState(false);
    const [isColumnsPanelOpen, _setIsColumnsPanelOpen] = useState(false);
    const [locallyFilteredData, setLocallyFilteredData] = useState([]);
    const [locallyFilteredTotal, setLocallyFilteredTotal] = useState(0);
    const [page, setPage] = useState(0);

    useEffect(() => {
        setFiltersBase(initialFilters || []);
    }, [initialFilters]);

    const setIsFiltersPanelOpen = (newState: boolean) => {
        if(newState && isColumnsPanelOpen) _setIsColumnsPanelOpen(false);
        _setIsFiltersPanelOpen(newState);
    }

    const setIsColumnsPanelOpen = (newState: boolean) => {
        if(newState && isFiltersPanelOpen) _setIsFiltersPanelOpen(false);
        _setIsColumnsPanelOpen(newState);
    }

    return (
        <TableContext.Provider value={{
            filtersBase,
            setFiltersBase,
            filters,
            setFilters,
            sortingBy,
            setSortingBy,
            sortingDirection,
            setSortingDirection,
            data,
            setData,
            locallyFilteredData,
            setLocallyFilteredData,
            isLoading,
            setIsLoading,
            total,
            setTotal,
            locallyFilteredTotal,
            setLocallyFilteredTotal,
            isFiltersPanelOpen,
            isColumnsPanelOpen,
            setIsFiltersPanelOpen,
            setIsColumnsPanelOpen,
            page,
            setPage,
            pageSize
        }}>
            {children}
        </TableContext.Provider>
    );
}